import React, { FC } from 'react';
import { AddRoleError, UpdateRoleError } from '@bechtel/types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  Spinner,
  Button,
  ButtonAppearance,
  theme,
} from '@bechtel/gds';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { t, Trans } from '@lingui/macro';
import { GET_ROLES } from '@bechtel/client-gql';

interface RoleErrorProps {
  roleError: AddRoleError | UpdateRoleError | ApolloError;
  onClose: () => void;
}

const getErrorMessage = (error: RoleErrorProps['roleError']) => {
  if (error instanceof ApolloError)
    return (
      <Trans>
        Network error has occured.Please press the &ldquo;Retry&rdquo; button to fetch the latest data and try again.
      </Trans>
    );
  switch (error) {
    case AddRoleError.NOT_FOUND:
      return (
        <Trans>
          Source role not found. Please press the &ldquo;Update&rdquo; button to fetch the latest data and try again.
        </Trans>
      );
    case UpdateRoleError.NOT_FOUND:
      return (
        <Trans>
          Role not found. Please press the &ldquo;Update&rdquo; button to fetch the latest data and try again.
        </Trans>
      );
    case UpdateRoleError.DEFAULT_ROLE_CAN_NOT_BE_MODIFIED:
      return (
        <Trans>
          Default role cannot be modified. Please press the &ldquo;Update&rdquo; button to fetch the latest data and try
          again.
        </Trans>
      );
    default:
      return (
        <Trans>
          An error has occured. Please press the &ldquo;Update&rdquo; button to fetch the latest data and try again.
        </Trans>
      );
  }
};

export const RoleError: FC<RoleErrorProps> = ({ roleError, onClose }) => {
  //Used useLazy instead of useRolesQueryHook as it will mandate the consumers to add useEffect
  //Also error is not in the main workflow for managing roles.
  const [loadRoles, { loading }] = useLazyQuery(GET_ROLES, {
    fetchPolicy: 'network-only',
  });

  return (
    <Modal visible={true} style={{ width: '552px', height: '275px' }}>
      <ModalHeader title={t`Role Error`}></ModalHeader>
      <ModalBody>
        <Box px={{ sm: '500' }} py={{ sm: '700' }}>
          <Text size="500" family="graphik" color="grey.1000" style={{ marginTop: `${theme.spacings[400]}` }}>
            {getErrorMessage(roleError)}
          </Text>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <Button appearance={ButtonAppearance.SECONDARY} onClick={onClose}>
              <Box px={{ sm: '500' }}>
                <Trans>Close</Trans>
              </Box>
            </Button>
          </Box>
          <Box>
            <Button
              type="submit"
              appearance={ButtonAppearance.SECONDARY}
              onClick={async () => {
                onClose();
              }}
              disabled={loading}
            >
              {loading && <Spinner></Spinner>}
              {!loading && <Box px={{ sm: '500' }}> {roleError instanceof ApolloError ? t`Retry` : t`Update`}</Box>}
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default RoleError;
